import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { ApplicationForm } from '../components/ApplicationForm';
import { ConciergeRequestBar } from '../components/ConciergeRequestBar';
import { PrivilegeIcon } from '../components/MenuGlyphs';
import { applicationHero, applicationLetter, applicationSteps, privileges } from '../data/application';
const ease = [0.22, 1, 0.36, 1] as const;
export function Apply() {
  const reduceMotion = useReducedMotion();
  const {
    hash
  } = useLocation();
  useEffect(() => {
    document.title = 'Membership — Superluxury';
    const target = hash ? document.getElementById(hash.slice(1)) : null;
    if (!target) {
      window.scrollTo(0, 0);
      return;
    }
    // Land directly on the requested section, after layout settles
    const id = window.setTimeout(() => target.scrollIntoView({
      block: 'start'
    }), 60);
    return () => window.clearTimeout(id);
  }, [hash]);
  const rise = (delay = 0) => ({
    initial: {
      opacity: 0,
      y: reduceMotion ? 0 : 18
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 1.2,
      ease,
      delay
    }
  });
  return <>
      <header className="absolute inset-x-0 top-0 z-40 text-paper" data-id="element-1370" data-mp="9127ly26lj06">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-6 md:h-20 md:px-16" data-id="element-1371" data-mp="8z7w1acsv38r">
          <Link to="/" className="inline-flex items-center gap-2 text-[14px] font-light text-paper/80 transition-colors duration-150 hover:text-paper" data-id="element-1372" data-mp="37h3ium6i5ppt">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-1373" data-mp="3hgc6yyeosa49" />
            <span className="hidden sm:inline" data-id="element-1374" data-mp="3m842lncp9619">Back to Superluxury</span>
          </Link>
          <Link to="/" aria-label="Superluxury — home" data-id="element-1375" data-mp="2osyepdlxo2qh">
            <Logo className="text-[16px] md:text-[20px]" data-id="element-1376" data-mp="2oqca9muca3ch" />
          </Link>
          <Link to="/private-line" className="text-[14px] font-light text-paper/80 transition-colors duration-150 hover:text-paper" data-id="element-1377" data-mp="2tpympu2cffk8">
            Private Line
          </Link>
        </div>
      </header>

      <main className="bg-paper text-ink" data-id="element-1378" data-mp="2hoqkuj9uixzv">
        {/* Tease */}
        <section aria-labelledby="apply-title" className="relative flex min-h-[480px] items-end overflow-hidden bg-ink text-paper md:min-h-[540px] lg:min-h-[56vh]" data-id="element-1379" data-mp="2vgws1fv0tun6">
          <motion.img src={applicationHero.image} alt={applicationHero.alt} className="absolute inset-0 h-full w-full object-cover" initial={{
          scale: reduceMotion ? 1 : 1.08,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 2.4,
          ease
        }} data-id="element-1380" data-mp="22rzka6ofrg8o" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.82),rgba(0,0,0,0.2)_55%,rgba(0,0,0,0.45))]" aria-hidden="true" data-id="element-1381" data-mp="27q110kye33gz" />
          <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.08] motion-reduce:hidden" aria-hidden="true" data-id="element-1382" data-mp="3j70tphonfkbj" />

          <div className="relative z-[1] mx-auto w-full max-w-[1440px] px-6 pb-10 pt-28 md:px-16 md:pb-12 md:pt-32" data-id="element-1383" data-mp="14wx11zbwd317">
            <motion.p {...rise(0.3)} className="text-[15px] font-light text-paper/80" data-id="element-1384" data-mp="19vgo63288kv5">
              Superluxury membership · By application
            </motion.p>
            <motion.h1 {...rise(0.45)} id="apply-title" className="mt-5 max-w-[920px] font-editorial text-[52px] leading-[54px] tracking-[-0.015em] md:text-[88px] md:leading-[88px]" data-id="element-1385" data-mp="10xo46llk8p1f">
              {applicationHero.title}
            </motion.h1>
            <motion.div {...rise(0.6)} className="mt-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-1386" data-mp="1evuwlm7qpwtp">
              <p className="max-w-[460px] text-[16px] font-light leading-[26px] text-paper/85" data-id="element-1387" data-mp="1jtwg2892vy5p">{applicationHero.line}</p>
              <a href="#application" className="group inline-flex items-center gap-3 self-start border border-paper/70 px-7 py-4 text-[16px] font-medium transition-colors duration-300 hover:bg-paper hover:text-ink md:self-auto" data-id="element-1388" data-mp="27jh06ugyx1gk">
                Begin your application
                <span aria-hidden="true" className="transition-transform duration-500 ease-luxe group-hover:translate-y-0.5" data-id="element-1389" data-mp="ep95940qfuo0">
                  ↓
                </span>
              </a>
            </motion.div>
          </div>
        </section>

        {/* What membership opens */}
        <section aria-labelledby="privileges-heading" className="px-6 py-14 md:px-16 md:py-20" data-id="element-1390" data-mp="16ijh2oklfvo4">
          <div className="mx-auto max-w-[1320px]" data-id="element-1391" data-mp="12hhepyvdr3hx">
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between" data-id="element-1392" data-mp="xnd6zdeairfs">
              <h2 id="privileges-heading" className="font-editorial text-[34px] leading-[38px] tracking-[-0.01em] md:text-[44px] md:leading-[48px]" data-id="element-1393" data-mp="so8y6nva2vpz">
                What membership opens
              </h2>
              <p className="text-[16px] font-light text-ink/65" data-id="element-1394" data-mp="1ld7gpm3bbzvt">One partner. Every door.</p>
            </div>

            <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1395" data-mp="1sqosny8h6d5v">
              {privileges.map((p, i) => <motion.li key={p.title} initial={{
              opacity: 0,
              y: reduceMotion ? 0 : 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.3
            }} transition={{
              duration: 1,
              ease,
              delay: i * 0.06
            }} className="group relative aspect-[4/3] overflow-hidden bg-mist lg:aspect-[4/5]" data-id="element-1396" data-mp="1xq1ov9l8kwl4" data-mpi={i}>
                  <img src={p.image} alt={p.alt} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-luxe group-hover:scale-[1.04]" data-id="element-1397" data-mp="2ld9kezsv4r9y" />
                  <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.75),rgba(0,0,0,0)_55%)]" aria-hidden="true" data-id="element-1398" data-mp="2ug2efatfjh3h" />
                  <div className="absolute inset-x-0 bottom-0 p-5 text-paper" data-id="element-1399" data-mp="zbb1af78m3c6">
                    <h3 className="flex items-center gap-2.5 text-[21px] font-normal leading-[26px]" data-id="element-1400" data-mp="z98es55amsc7">
                      <PrivilegeIcon title={p.title} className="h-[22px] w-[22px] shrink-0" data-id="element-1401" data-mp="v7mvo2261htk" />
                      {p.title}
                    </h3>
                    <p className="mt-1 text-[15px] font-light text-paper/80" data-id="element-1402" data-mp="14a5wkgme5kd6">{p.text}</p>
                  </div>
                </motion.li>)}
            </ul>
          </div>
        </section>

        {/* Application */}
        <section id="application" aria-labelledby="application-heading" className="scroll-mt-6 border-t border-line bg-paper px-6 py-20 md:px-16 md:py-28" data-id="element-1403" data-mp="1dzxrxd2behip">
          <div className="mx-auto grid max-w-[1320px] grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-1404" data-mp="1n0nju5ogfnr5">
            <aside className="lg:col-span-4" data-id="element-1405" data-mp="3tqjzdyzkfl09">
              <div className="lg:sticky lg:top-10" data-id="element-1406" data-mp="3ttfw5jzog3n4">
                <h2 id="application-heading" className="font-editorial text-[40px] leading-[44px] tracking-[-0.01em] md:text-[52px] md:leading-[56px]" data-id="element-1407" data-mp="3ot2070bcok5g">
                  Request membership
                </h2>
                <p className="mt-4 max-w-[380px] text-[16px] font-light leading-[26px] text-ink/70" data-id="element-1408" data-mp="2989gm7hzfwz3">
                  A few details are enough. Everything you share is read by a founding partner alone.
                </p>
                <div className="mt-10 hidden aspect-[4/5] max-w-[380px] overflow-hidden bg-mist lg:block" data-id="element-1409" data-mp="1zhokcqps74jc">
                  <img src={applicationLetter.image} alt={applicationLetter.alt} className="h-full w-full object-cover" data-id="element-1410" data-mp="1lif6vysymoj2" />
                </div>
                <ol className="mt-10 max-w-[380px] border-t border-line" data-id="element-1411" data-mp="1cjjxkdumcybj">
                  {applicationSteps.map((s, i) => <li key={s.title} className="flex gap-5 border-b border-line py-5" data-id="element-1412" data-mp="1glexbdwwj3n7" data-mpi={i}>
                      <span className="font-editorial text-[22px] leading-[26px] text-ink/40" data-id="element-1413" data-mp="36y4ecfftk9pn">{i + 1}</span>
                      <div data-id="element-1414" data-mp="3axd8hleru9lq">
                        <p className="text-[16px] font-medium" data-id="element-1415" data-mp="3awk8r4e8ckd3">{s.title}</p>
                        <p className="mt-1 text-[14px] font-light leading-[22px] text-ink/70" data-id="element-1416" data-mp="1yo00q7b5al4r">{s.text}</p>
                      </div>
                    </li>)}
                </ol>
              </div>
            </aside>
            <div className="lg:col-span-8" data-id="element-1417" data-mp="1ox5c4waff9g5">
              <ApplicationForm data-id="element-1418" data-mp="1jzdju15vdlbf" />
            </div>
          </div>
        </section>

        {/* Or a single request */}
        <section aria-labelledby="single-request-heading" className="border-t border-line bg-bone" data-id="element-1419" data-mp="1g0ehmobiw2no">
          <div className="mx-auto max-w-[1440px] px-6 pt-16 md:px-16 md:pt-20" data-id="element-1420" data-mp="1ay5mpmf3aqix">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between" data-id="element-1421" data-mp="161ow8lkf402o">
              <h2 id="single-request-heading" className="font-editorial text-[34px] leading-[38px] tracking-[-0.01em] md:text-[44px] md:leading-[48px]" data-id="element-1422" data-mp="112m4g2tg7qcn">
                Or begin with a single request
              </h2>
              <p className="max-w-[440px] text-[16px] font-light leading-[26px] text-ink/70" data-id="element-1423" data-mp="2vg3j10bvlcvj">
                Many members first came to us for one evening, one journey. Tell us where — we will take it from there.
              </p>
            </div>
          </div>
          <div className="mx-auto mt-10 max-w-[1440px] px-6 pb-20 md:px-16 md:pb-24" data-id="element-1424" data-mp="3coe96lyr3wzm">
            <ConciergeRequestBar idPrefix="apply-req" placement="membership_page" className="border border-line px-6 py-10 md:px-10 md:py-12" data-id="element-1425" data-mp="3hnppjtv7h0go" />
          </div>
        </section>
      </main>
    </>;
}